import { lowerFirst } from 'lodash'
import { ProductFeatureId } from '@/actions/permissionActions'
import { featureLabelForId } from '@/constants/productFeatureConstants'

export const REAL_ACCESS_FEATURE_IDS = [
  ProductFeatureId.managedBookkeeping,
  ProductFeatureId.annualTaxes,
  ProductFeatureId.quarterlyTaxes,
  ProductFeatureId.free2ndTaxFiling,
  ProductFeatureId.annualTaxesBusinessFiling,
  ProductFeatureId.phoneSupport,
  ProductFeatureId.payrollIncludedForFreeForSelf,
] as const

export type RealAccessFeatureId = (typeof REAL_ACCESS_FEATURE_IDS)[number]

const ACKNOWLEDGMENT_COPY: Record<RealAccessFeatureId, string> = {
  [ProductFeatureId.managedBookkeeping]:
    "Heard will stop doing your bookkeeping. You'll categorize transactions and keep your books up to date yourself.",
  [ProductFeatureId.annualTaxes]:
    "Heard will no longer file your annual tax return. You'll need to file on your own or with another preparer.",
  [ProductFeatureId.quarterlyTaxes]:
    "You'll stop getting quarterly tax estimates from Heard.",
  [ProductFeatureId.free2ndTaxFiling]:
    "Your plan includes a personal (1040) filing at no extra cost. You'll be giving that up, and filing personally with Heard later would be an added cost.",
  [ProductFeatureId.annualTaxesBusinessFiling]:
    "Your plan files your business return at no extra cost. You'll be giving that up, and filing it with Heard later would be an added cost.",
  [ProductFeatureId.phoneSupport]:
    "You'll lose scheduled support calls. Email support stays.",
  [ProductFeatureId.payrollIncludedForFreeForSelf]:
    "Payroll for one seat is included in your plan today at no extra cost. You'll start paying the standard payroll rate.",
}

const isRealAccessFeatureId = (id: string): id is RealAccessFeatureId =>
  (REAL_ACCESS_FEATURE_IDS as readonly string[]).includes(id)

/**
 * Plan-change wording only. The shared labels never say which return is which, so a customer
 * who already files with Heard reads the personal filing as something they have.
 */
const PLAN_CHANGE_FEATURE_LABELS: Record<string, string> = {
  [ProductFeatureId.annualTaxesBusinessFiling]:
    'Business tax return filed with Heard at no extra cost',
  [ProductFeatureId.free2ndTaxFiling]:
    'Personal tax return filed with Heard at no extra cost',
}

/** Verb-phrase labels read wrong after "giving up" or "lose"; lists and tables keep them. */
const LOSS_SENTENCE_LABELS: Record<string, string> = {
  [ProductFeatureId.annualTaxes]: 'Tax filing with Heard',
  [ProductFeatureId.payrollIncludedForFreeForSelf]:
    'Payroll for 1 seat at no extra cost',
  [ProductFeatureId.payroll]: 'Payroll for your practice',
}

export const planChangeFeatureLabel = (featureId: string): string =>
  PLAN_CHANGE_FEATURE_LABELS[featureId] ?? featureLabelForId(featureId)

export const lossSentenceLabel = (featureId: string): string =>
  lowerFirst(
    LOSS_SENTENCE_LABELS[featureId] ?? planChangeFeatureLabel(featureId)
  )

export const acknowledgmentCopyForFeature = (featureId: string): string =>
  isRealAccessFeatureId(featureId)
    ? ACKNOWLEDGMENT_COPY[featureId]
    : `You'll lose ${lossSentenceLabel(featureId)}.`
